import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface Content {
  activities: {
    title: string;
    description: string;
  };
  // Add other content fields as needed
  [key: string]: any;
}

interface ContentContextType {
  content: Content;
  loading: boolean;
  error: string | null;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

interface ContentProviderProps {
  children: ReactNode;
}

export const ContentProvider: React.FC<ContentProviderProps> = ({ children }) => {
  const [content, setContent] = useState<Content>({ activities: { title: '', description: '' } });
  const [loading, setLoading] = useState(false); // Começa como false para não bloquear
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        
        // Tenta importar Firebase apenas quando necessário
        let db;
        try {
          const firebaseModule = await import('../firebase');
          db = firebaseModule.db;
          
          if (!db) {
            throw new Error('Firebase não configurado');
          }
        } catch (firebaseError) {
          console.warn('Firebase não configurado, usando conteúdo padrão');
          // Usa conteúdo padrão se Firebase não estiver disponível
          setContent({
            activities: {
              title: 'Nossas Atividades',
              description: 'Conheça nossas atividades e projetos'
            }
          });
          setLoading(false);
          return;
        }

        // Importação dinâmica do Firestore
        const { doc, getDoc } = await import('firebase/firestore');
        
        const docRef = doc(db, 'content', 'pages');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setContent(docSnap.data() as Content);
        } else {
          // Usa conteúdo padrão se não encontrar no Firebase
          setContent({
            activities: {
              title: 'Nossas Atividades',
              description: 'Conheça nossas atividades e projetos'
            }
          });
        }
      } catch (err) {
        console.error('Erro ao buscar conteúdo:', err);
        setError('Failed to fetch content');
        // Usa conteúdo padrão em caso de erro
        setContent({
          activities: {
            title: 'Nossas Atividades',
            description: 'Conheça nossas atividades e projetos'
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  return (
    <ContentContext.Provider value={{ content, loading, error }}>
      {children}
    </ContentContext.Provider>
  );
};